import { Box, Button, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { CloseCircle, Instagram, TickCircle } from "iconsax-react";
import { useTranslation } from "react-i18next";

import { SefanoButton } from "@/components/Molecules";
import type { InstagramCallbackTemplateProps } from "./InstagramCallback.template.types";
import * as styles from "./InstagramCallback.template.styles";

export type { InstagramCallbackTemplateProps } from "./InstagramCallback.template.types";

export function InstagramCallbackTemplate({
  isLoading,
  isSuccess,
  isDenied,
  username,
  failureReasonKey,
  onManage,
  onGoToDashboard,
}: InstagramCallbackTemplateProps) {
  const { t } = useTranslation("instagram");
  const theme = useTheme();

  const tone = isLoading ? "neutral" : isSuccess ? "success" : "error";
  const title = isLoading
    ? t("callback.processing")
    : isSuccess
      ? t("callback.successTitle")
      : isDenied
        ? t("callback.deniedTitle")
        : t("callback.failedTitle");
  const description = isLoading
    ? t("callback.processingHint")
    : isSuccess
      ? username
        ? t("callback.successDescription", { username })
        : t("callback.successDescriptionNoName")
      : isDenied
        ? t("callback.deniedDescription")
        : t(failureReasonKey);

  return (
    <Box sx={styles.root}>
      <Box component="header" sx={styles.header}>
        <Box component="img" src="/images/nav_logo.png" alt="SEFANO" sx={styles.headerLogo} />
      </Box>

      <Box component="main" sx={styles.main}>
        <Box sx={styles.card}>
          <Box sx={styles.pair}>
            <Box sx={styles.brandTile}>
              <Box component="img" src="/images/logo.png" alt="SEFANO" sx={styles.brandLogo} />
            </Box>
            <Box sx={styles.link(tone)}>
              {tone === "neutral" ? (
                <Box sx={styles.spinner} />
              ) : tone === "success" ? (
                <TickCircle size={22} variant="Bold" color={theme.palette.success.main} />
              ) : (
                <CloseCircle size={22} variant="Bold" color={theme.palette.error.main} />
              )}
            </Box>
            <Box sx={styles.instagramTile}>
              <Instagram size={34} variant="Bold" color="currentColor" />
            </Box>
          </Box>

          <Typography component="h1" sx={styles.title}>
            {title}
          </Typography>
          <Typography sx={styles.description}>{description}</Typography>

          {isLoading ? null : (
            <Box sx={styles.actions}>
              <Button
                variant="outlined"
                color="primary"
                onClick={onGoToDashboard}
                sx={styles.outlinedButton}
              >
                {t("callback.goDashboard")}
              </Button>
              <SefanoButton onClick={onManage} sx={styles.actionButton}>
                {isSuccess ? t("callback.manage") : t("callback.tryAgain")}
              </SefanoButton>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
